import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ImageType, Product } from '@spartacus/core';
import { Subject, takeUntil } from 'rxjs';

import {
  BrandThemesEnum, IProductCustomer, SdBorderWrapperComponent, SdButtonComponent, SdListComponent,
  SdLoaderComponent, SdModalDefaultComponent, SdProductCustomerComponent, SdTitleComponent, ThemesTextEnum
} from '@seb/web-design-system/core';
import { SisCategoriesComponent } from '@seb/web-common/core/components/static/sis-categories/sis-categories.component';
import { Asset, CustomBaseStore } from '@seb/web-common/core/models';
import { HobMapper, ProductMapper } from '@seb/web-common/core/mappers';
import { BaseStoreService, ConfigurationService, ImageService, MyAccountService, TrackingService } from '@seb/web-common/core/services';
import { getMediaByFormat } from '@seb/web-common/core/utils';

@Component({
  selector: 'wc-my-account-products',
  templateUrl: './my-account-products.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdTitleComponent,
    SdBorderWrapperComponent,
    SdListComponent,
    SdProductCustomerComponent,
    SdButtonComponent,
    SisCategoriesComponent,
    SdLoaderComponent,
    SdModalDefaultComponent,
    TranslatePipe
  ]
})
export class MyAccountProductsComponent implements OnInit, OnDestroy {
  productCustomer: IProductCustomer[] = [];
  productCustomerExtern: IProductCustomer[] = [];
  productCustomerToSave: Product;
  productCustomerSelected: IProductCustomer;
  productCustomerDeleted: IProductCustomer;
  brandThemesEnum = BrandThemesEnum;
  themesTextEnum = ThemesTextEnum;
  isConfirmModalOpened = false;
  assetDeletedId: string;
  modalIsLoading = false;
  isLoading = true;
  brand: string;

  @ViewChild('productSelectedRef')
  productSelected: SdProductCustomerComponent;

  private readonly destroy$: Subject<void> = new Subject();

  private assets: Asset[];
  private baseStore: CustomBaseStore;

  constructor(
    private myAccountService: MyAccountService,
    private imageService: ImageService,
    private productMapper: ProductMapper,
    private router: Router,
    private configService: ConfigurationService,
    private baseStoreService: BaseStoreService,
    private trackingService: TrackingService,
    private translateService: TranslateService,
    private cdRef: ChangeDetectorRef,
    private hobMapper: HobMapper
  ) {}

  ngOnInit(): void {
    this.initAssets();
    this.baseStore = this.baseStoreService.baseStore;
  }

  initAssets(): void {
    this.myAccountService.getAssets()
      .pipe(takeUntil(this.destroy$))
      .subscribe((assets) => {
        const config = this.configService.config;
        this.brand = config.myAccount.brand;
        this.assets = assets;

        this.productCustomer = assets
          .filter((asset) => !!asset && asset.brand === this.brand)
          .map((asset) => this.assetToProductCustomer(asset));
        this.productCustomerExtern = assets
          .filter((asset) => !!asset && asset.brand !== this.brand)
          .map((asset) => this.assetToProductCustomer(asset));
        this.isLoading = false;
        this.cdRef.markForCheck();
      });
  }

  productWarranty(): void {
    this.router.navigateByUrl(this.translateService.instant('myProduct.warrantyUrl'));
  }

  productInstruction(product: IProductCustomer): void {
    const productCode = this.assets.find((asset) => asset.commercialCode === product.code)?.cmmf;
    this.router.navigate([`/p/${productCode}`], {
      fragment: 'ifu-list'
    });
  }

  deleteProduct(product: IProductCustomer): void {
    this.assetDeletedId = this.assets.find((asset) => asset.commercialCode === product.code)?.id;
    this.productCustomerDeleted = { ...product };
    this.isConfirmModalOpened = true;
  }

  cancelSwitch(): void {
    this.isConfirmModalOpened = false;
    this.modalIsLoading = false;
  }

  confirmDeleteAsset(): void {
    this.modalIsLoading = true;
    this.myAccountService
      .deleteAsset(this.assetDeletedId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.isConfirmModalOpened = false;
          this.initAssets();
          this.modalIsLoading = false;
          this.cdRef.markForCheck();
        },
        () => (this.modalIsLoading = false)
      );
  }
  transformDateString(inputDateString: string) {
    const [day, month, year] = inputDateString.replace(/(\d{4}).*/, '$1').split(/[./]/).map(Number);
    const dateObject = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
    const isoString = dateObject.toISOString();
    return isoString;
  }

  addProductAsset(): void {
    let inputDateString = '';
    if (this.productSelected.selectedDate) {
      const selectedDate = this.productSelected.selectedDate;
      inputDateString = new Date(`${selectedDate.year}-${selectedDate.month}-${selectedDate.day}`).toLocaleDateString(
        this.baseStore.market.toLocaleLowerCase()
      );
    } else {
      inputDateString = new Date(
        `${this.productSelected.defaultSelectedYear.value}-${this.productSelected.defaultSelectedMonth.value}-${this.productSelected.defaultSelectedDay.value}`
      ).toLocaleDateString(this.baseStore.market.toLocaleLowerCase());
    }
    const formattedDate = this.transformDateString(inputDateString);

    const imageUrl = this.imageService.normalizeImageUrl(
      getMediaByFormat(this.productCustomerToSave?.primaryImages, 'main')?.url ??
      this.productCustomerToSave?.images[ImageType.PRIMARY]?.['main']?.originalUrl
    );
    const data: Asset = {
      commercialCode: this.productCustomerToSave?.commercialCode,
      name: this.productCustomerToSave?.name,
      sourceSystem: 'Hybris',
      purchaseDate: formattedDate,
      cmmf: this.productCustomerToSave?.code,
      productPicture: imageUrl,
      market: this.baseStore.market,
      productUrl: document.baseURI + 'faq/csp/' + this.productCustomerToSave.code
    };
    this.myAccountService
      .addAsset(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.productCustomerSelected = undefined;
        this.initAssets();
        this.router.navigateByUrl('/my-products');
        this.trackingService.sendEvent('clickRegisterProduct', {
          productName: data.name ? data.name : '',
          productID: data.cmmf ? data.cmmf : ''
        });
        this.cdRef.markForCheck();
      });
  }

  selectProduct(product: Product): void {
    this.productMapper
      .productToProductCustomer(product)
      .pipe(takeUntil(this.destroy$))
      .subscribe((productCustomer) => {
        this.productCustomerToSave = product;
        this.productCustomerSelected = productCustomer;
        this.cdRef.markForCheck();
      });
  }

  cancelSave(): void {
    this.productCustomerSelected = undefined;
    this.router.navigateByUrl('/my-products');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private assetToProductCustomer(asset: Asset): IProductCustomer {
    let media;
    const baseStore = this.baseStoreService.baseStore;
    const isHob = !!baseStore.houseOfBrandConfig?.brand?.length;
    let productUrl = asset.productUrl;

    if (asset.productPicture?.includes('?context=')) {
      const imageContext = asset.productPicture?.split('?context=')[1];
      media = { url: '/medias/?context=' + imageContext };
    } else {
      media = { url: asset.productPicture };
    }

    if (productUrl?.includes('brandsites.api.groupe-seb.com')) {
      productUrl = productUrl
        .replace('brandsites.api.groupe-seb.com', `${asset.brand}.${asset.market.toLowerCase()}`)
        .replace(/(\/\/faq\/\/)|(\/\/faq\/)/, '/faq/');
    }

    if (productUrl?.includes('faq//')) {
      productUrl = productUrl.replace('faq//', 'faq/');
    }
    return {
      code: asset.commercialCode,
      title: asset.name,
      media: this.imageService.normalizeMedia(media),
      brandLogoTitle: isHob ? this.hobMapper.getBrandLogoTitle(asset.brand) : null,
      url: productUrl?.replace('/p/', '/csp/'),
      purchasedDate: new Date(asset.purchaseDate).toLocaleDateString(this.baseStore.market.toLocaleLowerCase())
    };
  }
}
